import {
  PreScreeningQuestion,
  QuestionType,
  RadioGridQuickSelect,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  DIABETES_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  GASTROINTESTINAL_ATTRIBUTES,
  GLP1_MEDICATIONS,
  HORMONE_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Amgen CVD Risk Study (20220196)
 * 
 * Target population: Adults 45+ with BMI ≥27 and established CVD risk factors
 * Key inclusion criteria:
 * - Age 45+
 * - BMI ≥27
 * - Previous heart attack, stroke, or peripheral artery disease
 * - Type 2 diabetes OR no diabetes (Type 1 excluded)
 * - Not currently on GLP-1 medications
 */
export const amgenCVDRiskScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      { value: "45_or_older", label: "45 or older" },
      { 
        value: "under_45", 
        label: "Under 45",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study is only for adults who are 45 years of age or older.",
    attributeMapping: mappers.value({
      "45_or_older": [],
      under_45: [],
    }),
  },
  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },
  {
    ...standardWeightQuestion,
    minBMI: 27,
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },
  {
    id: "cardiovascular_history",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following?",
    required: true,
    allowBack: true,
    options: [
      { 
        value: "previous_heart_attack", 
        label: "Previous heart attack",
      },
      { 
        value: "previous_stroke", 
        label: "Previous ischaemic stroke (stroke caused by a clot in the brain, or a bleed in the brain following a clot)",
      },
      { 
        value: "peripheral_artery_disease", 
        label: "Peripheral artery disease (narrowing of the arteries of the limbs causing pain or requiring a procedure to widen the artery or amputation)",
      },
      { 
        value: "none_of_above", 
        label: "None of the above",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study is only for people who have been diagnosed with cardiovascular disease.",
    attributeMapping: mappers.value({
      previous_heart_attack: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
      ],
      previous_stroke: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
      ],
      peripheral_artery_disease: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: true },
      ],
      none_of_above: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "diabetes_status",
    type: QuestionType.RADIO,
    question: "Do you have diabetes?",
    required: true,
    allowBack: true,
    options: [
      { 
        value: "type1_diabetes", 
        label: "Yes, I have type 1 diabetes",
        disqualify: true,
      },
      { 
        value: "type2_diabetes", 
        label: "Yes, I have type 2 diabetes",
      },
      { 
        value: "no_diabetes", 
        label: "No, I don't have diabetes",
      },
    ],
    disqualifyMessage:
      "This study cannot include people with type 1 diabetes.",
    attributeMapping: mappers.value({
      type1_diabetes: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: true },
      ],
      type2_diabetes: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
      ],
      no_diabetes: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "type2_diabetes_complications",
    type: QuestionType.CHECKBOX,
    question: "If you answered YES to having type 2 diabetes, are any of the following true regarding your type 2 diabetes? If you do NOT have type 2 diabetes, please select none of the above:",
    required: true,
    allowBack: true,
    options: [
      {
        value: "a1c_over_10_5",
        label: "My A1C level is >10.5%",
        disqualify: true,
      },
      {
        value: "dka_honk_12_months",
        label: "I have had an episode of diabetic ketoacidosis (DKA) or hyperosmolar coma (HONK/HHS) in the past 12 months",
        disqualify: true,
      },
      {
        value: "severe_hypoglycemia_6_months",
        label: "I have had an episode of severe hypoglycaemia (low blood sugar) in the past 6 months",
        disqualify: true,
      },
      {
        value: "no_hypoglycemia_symptoms",
        label: "I don't notice any symptoms when my sugar levels go low",
        disqualify: true,
      },
      {
        value: "diabetic_eye_changes",
        label: "I have diabetic changes in my eyes",
        disqualify: true,
      },
      {
        value: "insulin_pump_carb_counting",
        label: "I use an insulin pump and/or count carbs for bolus dosing of insulin",
        disqualify: true,
      },
      {
        value: "none_of_above",
        label: "None of the above is true",
      },
    ],
    disqualifyMessage:
      "This study cannot include people with these diabetes complications.",
    attributeMapping: mappers.value({
      a1c_over_10_5: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: true },
      ],
      dka_honk_12_months: [],
      severe_hypoglycemia_6_months: [],
      no_hypoglycemia_symptoms: [],
      diabetic_eye_changes: [],
      insulin_pump_carb_counting: [],
      none_of_above: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "pancreatic_thyroid_conditions",
    type: QuestionType.CHECKBOX,
    question: "Are any of these statements true for you?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "chronic_pancreatitis",
        label: "I have chronic pancreatitis",
        disqualify: true,
      },
      {
        value: "acute_pancreatitis_6_months",
        label: "I have had acute pancreatitis in the past 6 months",
        disqualify: true,
      },
      {
        value: "mtc_men2_history",
        label: "I have or a first degree relative has medullary thyroid carcinoma (MTC) or multiple endocrine neoplasia type 2 (MEN-2)",
        disqualify: true,
      },
      {
        value: "bariatric_surgery_recent",
        label: "I have had bariatric surgery within the past 6 months or am planning to have it soon",
        disqualify: true,
      },
      {
        value: "none_of_above",
        label: "None of the above",
      },
    ],
    disqualifyMessage:
      "This study cannot include people with these conditions.",
    attributeMapping: mappers.value({
      chronic_pancreatitis: [
        { attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_PANCREATITIS, booleanAttributeValue: true },
      ],
      acute_pancreatitis_6_months: [
        { attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_PANCREATITIS, booleanAttributeValue: true },
      ],
      mtc_men2_history: [],
      bariatric_surgery_recent: [],
      none_of_above: [
        { attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_PANCREATITIS, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "serious_medical_conditions",
    type: QuestionType.CHECKBOX,
    question: "Are any of these statements true for you?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "liver_failure_cirrhosis",
        label: "I have liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "kidney_failure_dialysis",
        label: "I have end stage kidney failure or am on dialysis",
        disqualify: true,
      },
      {
        value: "cancer_5_years",
        label: "I have been treated for cancer in the past 5 years (excluding BCC, SCC, breast DCIS, in situ cervical cancer and in-situ prostate cancer)",
        disqualify: true,
      },
      {
        value: "organ_transplant",
        label: "I have had an organ transplant (not including corneal transplant)",
        disqualify: true,
      },
      {
        value: "pregnant_breastfeeding",
        label: "I am currently pregnant or breastfeeding",
        disqualify: true,
      },
      {
        value: "none_of_above",
        label: "None of the above",
      },
    ],
    disqualifyMessage:
      "This study cannot include people with these serious medical conditions.",
    attributeMapping: mappers.value({
      liver_failure_cirrhosis: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: true },
      ],
      kidney_failure_dialysis: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
      ],
      cancer_5_years: [
        { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
      ],
      organ_transplant: [
        { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true },
      ],
      pregnant_breastfeeding: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: true },
      ],
      none_of_above: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
        { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
        { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false },
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "glp1_medications",
    type: QuestionType.RADIO_GRID,
    question: "Have you taken any of these GLP-1 receptor agonist medications?",
    helpText: "GLP-1 receptor agonists are given as injections or tablets and include medications like Ozempic, Wegovy, Rybelsus, Mounjaro, and Zepbound.",
    required: true,
    allowBack: true,
    quickSelect: RadioGridQuickSelect.createMedicationQuickSelect(),
    gridLayout: {
      columnHeaders: ["Currently taking", "Never taken", "Stopped 3 months ago or less", "Stopped 4 months ago or more"],
      rows: [
        {
          label: "Ozempic, Wegovy or Rybelsus",
          sublabel: "Semaglutide",
          values: [
            { value: "semaglutide_current", disqualify: true },
            { value: "semaglutide_never" },
            { value: "semaglutide_stopped_3_months", disqualify: true },
            { value: "semaglutide_stopped_4_months" },
          ],
        },
        {
          label: "Mounjaro or Zepbound",
          sublabel: "Tirzepatide",
          values: [
            { value: "tirzepatide_current", disqualify: true },
            { value: "tirzepatide_never" },
            { value: "tirzepatide_stopped_3_months", disqualify: true },
            { value: "tirzepatide_stopped_4_months" },
          ],
        },
        {
          label: "Victoza or Saxenda",
          sublabel: "Liraglutide",
          values: [
            { value: "liraglutide_current", disqualify: true },
            { value: "liraglutide_never" },
            { value: "liraglutide_stopped_3_months", disqualify: true },
            { value: "liraglutide_stopped_4_months" },
          ],
        },
        {
          label: "Trulicity",
          sublabel: "Dulaglutide",
          values: [
            { value: "dulaglutide_current", disqualify: true },
            { value: "dulaglutide_never" },
            { value: "dulaglutide_stopped_3_months", disqualify: true },
            { value: "dulaglutide_stopped_4_months" },
          ],
        },
        {
          label: "Byetta or Bydureon",
          sublabel: "Exenatide",
          values: [
            { value: "exenatide_current", disqualify: true },
            { value: "exenatide_never" },
            { value: "exenatide_stopped_3_months", disqualify: true },
            { value: "exenatide_stopped_4_months" },
          ],
        },
      ],
    },
    disqualifyMessage:
      "This study cannot include people who are currently taking or recently stopped GLP-1 medications.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        semaglutide: GLP1_MEDICATIONS.SEMAGLUTIDE,
        tirzepatide: GLP1_MEDICATIONS.TIRZEPATIDE,
        liraglutide: GLP1_MEDICATIONS.LIRAGLUTIDE,
        dulaglutide: GLP1_MEDICATIONS.DULAGLUTIDE,
        exenatide: GLP1_MEDICATIONS.EXENATIDE,
      },
    }),
  },
  {
    id: "weight_loss_medications",
    type: QuestionType.RADIO_GRID,
    question: "Have you taken any of these weight loss medications?",
    helpText: "These are medications specifically prescribed for weight management or diabetes control.",
    required: true,
    allowBack: true,
    quickSelect: RadioGridQuickSelect.createMedicationQuickSelect(),
    gridLayout: {
      columnHeaders: ["Currently taking", "Never taken", "Stopped 3 months ago or less", "Stopped 4 months ago or more"],
      rows: [
        {
          label: "Pramlintide (Symlin)",
          sublabel: "Pramlintide",
          values: [
            { value: "pramlintide_current", disqualify: true },
            { value: "pramlintide_never" },
            { value: "pramlintide_stopped_3_months", disqualify: true },
            { value: "pramlintide_stopped_4_months" },
          ],
        },
        {
          label: "Phentermine (Adipex-P, Lomaira)",
          sublabel: "Phentermine",
          values: [
            { value: "phentermine_current", disqualify: true },
            { value: "phentermine_never" },
            { value: "phentermine_stopped_3_months", disqualify: true },
            { value: "phentermine_stopped_4_months" },
          ],
        },
        {
          label: "Orlistat (Xenical, Alli)",
          sublabel: "Orlistat",
          values: [
            { value: "orlistat_current", disqualify: true },
            { value: "orlistat_never" },
            { value: "orlistat_stopped_3_months", disqualify: true },
            { value: "orlistat_stopped_4_months" },
          ],
        },
        {
          label: "Contrave (Naltrexone/Bupropion)",
          sublabel: "Naltrexone/Bupropion",
          values: [
            { value: "contrave_current", disqualify: true },
            { value: "contrave_never" },
            { value: "contrave_stopped_3_months", disqualify: true },
            { value: "contrave_stopped_4_months" },
          ],
        },
      ],
    },
    disqualifyMessage:
      "This study cannot include people who are currently taking or recently stopped weight loss medications.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        // TODO: Add proper attribute IDs when available - using placeholder for now
        pramlintide: 999999,
        phentermine: 999999,
        orlistat: 999999,
        contrave: 999999,
      },
    }),
  },
  {
    id: "study_visit_availability",
    type: QuestionType.RADIO,
    question: "Are you willing to attend regular in-person visits?",
    helpText: "This study involves in-person visits, telehealth consults as well as administration of a self-injected medication.",
    required: true,
    allowBack: true,
    options: [
      { 
        value: "yes_willing", 
        label: "Yes, I am willing to attend in-person visits",
      },
      { 
        value: "no_cannot_commit", 
        label: "No, I cannot commit to in-person visits",
        disqualify: true,
      },
      { 
        value: "discuss_schedule", 
        label: "I would like to discuss the schedule further with the study team",
      },
    ],
    disqualifyMessage:
      "This study requires regular in-person visits and telehealth consultations.",
    attributeMapping: mappers.value({
      yes_willing: [],
      no_cannot_commit: [],
      discuss_schedule: [],
    }),
  },
  {
    id: "current_clinical_trial",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in a clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_in_trial",
        label: "I am currently enrolling in or participating in a clinical trial",
        disqualify: true,
      },
      { 
        value: "no_not_in_trial", 
        label: "I am not currently enrolling in or participating in a clinical trial",
      },
    ],
    disqualifyMessage:
      "You can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      yes_in_trial: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
      no_not_in_trial: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
    }),
  },
];